import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import type {
  AdminNumbersResponse,
  AdminNumberCreateRequest,
  AdminOracordNumberPurchaseResponse,
  AdminNumberRow,
  AdminStaffProfile,
  ServerBillingStatus,
} from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { useMe } from '../hooks/useMe';
import { useSessionStore } from '../stores/session';
import {
  activeStaffProfiles,
  staffLabel,
  staffProfileMapById,
  staffProfileOptionLabel,
  staffWorkEmailLabel,
} from '../lib/staff';
import { fetchStaffProfiles } from '../lib/staff-api';
import {
  matchesPhoneNumber,
  matchesStaffProfileId,
  readNumberDrillthroughParams,
} from '../lib/assignment-drillthrough';
import { businessLineSlotSummary } from '../lib/business-line-slots';

type Role = 'owner' | 'admin' | 'member';

export default function Numbers() {
  const [searchParams] = useSearchParams();
  const { me } = useMe();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const active = me?.organizations.find(o => o.id === activeOrgId);
  const myRole = (active?.role ?? 'member') as Role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const [rows, setRows] = useState<AdminNumberRow[]>([]);
  const [staffProfiles, setStaffProfiles] = useState<AdminStaffProfile[]>([]);
  const [billingStatus, setBillingStatus] = useState<ServerBillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeProfiles = useMemo(() => activeStaffProfiles(staffProfiles), [staffProfiles]);
  const profilesById = useMemo(() => staffProfileMapById(activeProfiles), [activeProfiles]);
  const drillthrough = useMemo(() => readNumberDrillthroughParams(searchParams), [searchParams]);
  const visibleRows = useMemo(
    () => rows.filter(row => matchesStaffProfileId(row.staffProfileId, drillthrough.staffProfileId)),
    [rows, drillthrough.staffProfileId],
  );
  const slotSummary = useMemo(
    () => businessLineSlotSummary({ plan: billingStatus?.plan, used: rows.length }),
    [billingStatus?.plan, rows.length],
  );

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const [numbersRes, profiles, billing] = await Promise.all([
        apiFetch<AdminNumbersResponse>('/api/admin/numbers'),
        fetchStaffProfiles(),
        apiFetch<ServerBillingStatus>('/api/billing/status'),
      ]);
      setRows(numbersRes.rows);
      setStaffProfiles(profiles);
      setBillingStatus(billing);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Business lines could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!activeOrgId) {
    return <div className="text-sm text-slate-500">No organization selected.</div>;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-12">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Business lines</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Each business line is one team member’s Oracord phone slot: a number, call/message history, recordings, transcripts, and storage.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-6 shadow-sm shadow-emerald-100/70">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Plan slots</p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950">{slotSummary.title}</h2>
            <p className="mt-2 max-w-2xl text-sm text-emerald-950/80">{slotSummary.helper}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center sm:min-w-80">
            <SlotMetric label="Included" value={slotSummary.includedSlots} />
            <SlotMetric label="Active" value={slotSummary.usedSlots} />
            <SlotMetric label="Open" value={slotSummary.openSlots} />
          </div>
        </div>
      </section>

      {canManage && (
        <ActivateBusinessLineForm
          staffProfiles={activeProfiles}
          initialStaffProfileId={drillthrough.staffProfileId}
          openSlots={slotSummary.openSlots}
          includedSlots={slotSummary.includedSlots}
          onDone={load}
        />
      )}

      <section className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm shadow-slate-200/70">
        <header className="flex flex-col gap-2 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">Active lines</h2>
            <p className="mt-1 text-sm text-slate-500">
              {loading && rows.length === 0
                ? 'Loading…'
                : `${visibleRows.length} active line${visibleRows.length === 1 ? '' : 's'}`}
            </p>
          </div>
          {drillthrough.staffProfileId && (
            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
              Filtered from Team
            </span>
          )}
        </header>

        {loading && rows.length === 0 ? (
          <div className="px-6 py-8 text-sm text-slate-500">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <EmptyState canManage={canManage} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleRows.map(row => (
              <BusinessLineItem
                key={row.phoneNumber}
                row={row}
                profile={row.staffProfileId ? profilesById.get(row.staffProfileId) : null}
                staffProfiles={activeProfiles}
                highlighted={matchesPhoneNumber(row.phoneNumber, drillthrough.phoneNumber) && Boolean(drillthrough.phoneNumber)}
                canManage={canManage}
                onChanged={load}
              />
            ))}
          </ul>
        )}
      </section>

      {canManage && (
        <ExistingNumberDetails
          staffProfiles={activeProfiles}
          initialStaffProfileId={drillthrough.staffProfileId}
          onDone={load}
        />
      )}
    </div>
  );
}

function SlotMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-emerald-100 bg-white/80 px-4 py-3">
      <div className="text-2xl font-semibold text-slate-950">{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-emerald-700">{label}</div>
    </div>
  );
}

function ActivateBusinessLineForm({
  staffProfiles,
  initialStaffProfileId,
  openSlots,
  includedSlots,
  onDone,
}: {
  staffProfiles: AdminStaffProfile[];
  initialStaffProfileId: string | null;
  openSlots: number;
  includedSlots: number;
  onDone: () => void | Promise<void>;
}) {
  const [staffProfileId, setStaffProfileId] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const canActivate = staffProfiles.length > 0 && openSlots > 0;

  useEffect(() => {
    if (!canActivate || staffProfileId) return;
    const preferred = initialStaffProfileId && staffProfiles.some(profile => profile.id === initialStaffProfileId)
      ? initialStaffProfileId
      : staffProfiles[0]?.id;
    setStaffProfileId(preferred ?? '');
  }, [canActivate, initialStaffProfileId, staffProfileId, staffProfiles]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canActivate) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const response = await apiFetch<AdminOracordNumberPurchaseResponse>('/api/admin/oracord-numbers/purchase', {
        method: 'POST',
        body: {
          staffProfileId: staffProfileId.trim(),
          ...(areaCode.trim() ? { areaCode: areaCode.trim() } : {}),
        },
      });
      setAreaCode('');
      const assignedProfile = staffProfiles.find(p => p.id === response.number.staffProfileId);
      setSuccess(`${response.number.phoneNumber} activated${assignedProfile ? ` for ${staffProfileOptionLabel(assignedProfile)}` : ''}.`);
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Business line could not be activated.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm shadow-slate-200/70">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-950">Set up a business line</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Pick the person, optionally choose an area code, and Oracord will provision the number into an included slot when one is open.
          </p>
        </div>
        <div className={`rounded-full px-3 py-1 text-xs font-semibold ${openSlots > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
          {openSlots > 0 ? 'Included slot available' : 'No open included slots'}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {success}
        </div>
      )}
      {staffProfiles.length === 0 && (
        <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Add a Team member before setting up a business line.
        </div>
      )}
      {includedSlots === 0 && (
        <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Choose a plan before provisioning Oracord-managed numbers.
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-5 grid gap-3 lg:grid-cols-[1fr_8rem_auto] lg:items-end">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Person</span>
          <select
            required
            disabled={!canActivate}
            value={staffProfileId}
            onChange={e => setStaffProfileId(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
          >
            {staffProfiles.map(profile => (
              <option key={profile.id} value={profile.id}>{staffProfileOptionLabel(profile)}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">Area code</span>
          <input
            value={areaCode}
            onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
            placeholder="604"
            inputMode="numeric"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <button
          type="submit"
          disabled={submitting || !canActivate}
          className="rounded-xl bg-slate-950 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
        >
          {submitting ? 'Setting up…' : 'Set up line'}
        </button>
      </form>
      <p className="mt-3 text-xs text-slate-500">
        Numbers are not pre-bought at signup. Oracord provisions a number only when you activate a line.
      </p>
    </section>
  );
}

function ExistingNumberDetails({
  staffProfiles,
  initialStaffProfileId,
  onDone,
}: {
  staffProfiles: AdminStaffProfile[];
  initialStaffProfileId: string | null;
  onDone: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [staffProfileId, setStaffProfileId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAdd = staffProfiles.length > 0;

  useEffect(() => {
    if (!canAdd || staffProfileId) return;
    const preferred = initialStaffProfileId && staffProfiles.some(profile => profile.id === initialStaffProfileId)
      ? initialStaffProfileId
      : staffProfiles[0]?.id;
    setStaffProfileId(preferred ?? '');
  }, [canAdd, initialStaffProfileId, staffProfileId, staffProfiles]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canAdd) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: AdminNumberCreateRequest = {
        phoneNumber: phoneNumber.trim(),
        staffProfileId: staffProfileId.trim(),
      };
      await apiFetch('/api/admin/numbers', { method: 'POST', body });
      setPhoneNumber('');
      setOpen(false);
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message || 'Number could not be saved.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm shadow-slate-200/70">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className="flex w-full items-center justify-between text-left"
      >
        <span>
          <span className="block text-sm font-semibold text-slate-950">Already have a business number?</span>
          <span className="mt-1 block text-sm text-slate-500">Map an existing number for testing or migration. This does not buy a number.</span>
        </span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="mt-5">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {!canAdd && (
            <div className="mb-4 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Add a Team member before mapping a number.
            </div>
          )}
          <form onSubmit={onSubmit} className="grid gap-3 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Phone number</span>
              <input
                required
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                placeholder="+1 778 555 1234"
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">Person</span>
              <select
                required
                disabled={!canAdd}
                value={staffProfileId}
                onChange={e => setStaffProfileId(e.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              >
                {staffProfiles.map(profile => (
                  <option key={profile.id} value={profile.id}>{staffProfileOptionLabel(profile)}</option>
                ))}
              </select>
            </label>

            <button
              type="submit"
              disabled={submitting || !canAdd}
              className="rounded-xl border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 shadow-sm hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Map number'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}

function EmptyState({ canManage }: { canManage: boolean }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="text-sm font-medium text-slate-900">No active lines yet</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500">
        {canManage ? 'Set up the first line above when you are ready.' : 'Only owners and admins can set up business lines.'}
      </p>
    </div>
  );
}

function BusinessLineItem({
  row,
  profile,
  staffProfiles,
  highlighted,
  canManage,
  onChanged,
}: {
  row: AdminNumberRow;
  profile?: AdminStaffProfile | null;
  staffProfiles: AdminStaffProfile[];
  highlighted?: boolean;
  canManage: boolean;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(row.staffProfileId ?? '');
  const ownerLabel = staffLabel({
    profile,
    legacyStaffName: row.staffName,
    legacyStaffIdentity: row.staffIdentity,
    fallback: 'Unassigned',
  });
  const workEmail = staffWorkEmailLabel(profile);

  useEffect(() => {
    setSelectedProfileId(row.staffProfileId ?? '');
  }, [row.staffProfileId]);

  async function onUpdateStaff() {
    if (!selectedProfileId || selectedProfileId === (row.staffProfileId ?? '')) return;
    setBusy(true);
    try {
      await apiFetch('/api/admin/numbers', {
        method: 'POST',
        body: { phoneNumber: row.phoneNumber, staffProfileId: selectedProfileId } satisfies AdminNumberCreateRequest,
      });
      await onChanged();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : (err as Error).message || 'Line could not be updated.');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!confirm(`Remove ${row.phoneNumber}?`)) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/numbers/${encodeURIComponent(row.phoneNumber)}`, { method: 'DELETE' });
      await onChanged();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : (err as Error).message || 'Number could not be removed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={`px-6 py-4 ${highlighted ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'bg-white'}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-mono text-base font-semibold text-slate-950">{row.phoneNumber}</div>
          <div className="mt-1 text-sm text-slate-500">Used by <span className="font-medium text-slate-800">{ownerLabel}</span></div>
          {workEmail && <div className="mt-1 text-sm text-slate-500">Work email {workEmail}</div>}
          <div className="mt-2 flex flex-wrap gap-2">
            {row.source === 'oracord_managed' ? (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                Included business line
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">Existing number</span>
            )}
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${row.providerStatus === 'provisioned' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {row.providerStatus === 'provisioned' ? 'Active' : 'Mapped'}
            </span>
          </div>
        </div>
        {canManage && (
          <div className="flex flex-col gap-2 sm:min-w-80 sm:items-end">
            <label className="w-full text-left sm:text-right">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Person</span>
              <select
                value={selectedProfileId}
                onChange={e => setSelectedProfileId(e.target.value)}
                disabled={busy || staffProfiles.length === 0}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
              >
                {!selectedProfileId && <option value="">Choose person</option>}
                {staffProfiles.map(staffProfile => (
                  <option key={staffProfile.id} value={staffProfile.id}>{staffProfileOptionLabel(staffProfile)}</option>
                ))}
              </select>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onUpdateStaff}
                disabled={busy || !selectedProfileId || selectedProfileId === (row.staffProfileId ?? '')}
                className="inline-flex items-center justify-center rounded-xl bg-slate-950 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 disabled:opacity-40"
              >
                Update
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}
